import { toast } from '../ui/toast/sonner';
import {
  TransactionPage,
  Transaction,
  Action,
  Bucket,
  TransactionListRow,
  TransactionDateHeader,
  UpdateTransactionPayload,
  DeleteTransferActionTransaction,
  EditManyTransactionsActionValue,
} from '../types';
import {
  Observable,
  Subscription,
  ReplaySubject,
  from,
  Subject,
  of,
  BehaviorSubject,
  combineLatest,
  EMPTY,
  defer,
} from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { CollectionViewer } from '@angular/cdk/collections';
import { TransactionService } from '../services/transaction.service';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BucketService } from '../services/bucket.service';
import { deepEqual } from '../shared/utils/deep-equal';
import { DateTime } from 'luxon';
import { hasTransactionSyncedFinancialFacts } from './transaction-plaid-account-display';

export interface TransactionMutationActions {
  updateTransaction(data: UpdateTransactionPayload): Promise<Transaction>;
  updateMultipleTransactions(
    data: EditManyTransactionsActionValue,
    previousTransactions: Array<Pick<Transaction, 'bucketId' | 'date'>>,
  ): Promise<{ data: Transaction[] }>;
  deleteTransaction(transactionId: string, monthYear?: string): Promise<void>;
  deleteTransfer(transferId: string, monthYear?: string): Promise<void>;
  undeleteTransaction(transactionId: string, monthYear?: string): Promise<void>;
}

export type CategorizationMethod = 'single_edit' | 'bulk_edit' | 'accept_pending';

export interface CategorizationOutcome {
  /** Imported transactions whose category actually changed. */
  transactionCount: number;
  method: CategorizationMethod;
  /** How many of those landed in a bucket the app had suggested for that transaction. */
  suggestedCount: number;
}

export interface FailedTransactionAction {
  action: Exclude<Action['type'], 'SCROLL_EVENT'>;
  /** HTTP status when the API answered; null for network or client failures. */
  status: number | null;
}

export interface TransactionDataSourceOptions {
  withoutDateHeaders: boolean;
  loadFirstPageOnly: boolean;
  loadMoreTransactions?: () => Promise<TransactionPage | null | undefined>;
  refreshTransactions?: () => Promise<TransactionPage | null | undefined>;
  refreshSession?: () => Promise<unknown>;
  /** Optimistic badge hook: fires at dispatch, and with the opposite sign if that mutation fails. */
  onUnsortedCountChange?: (delta: number) => void;
  /** Fires once after a successful UI categorization changes one or more imported transactions. */
  onSuccessfulCategorization?: (outcome: CategorizationOutcome) => void;
  /** Fires after the built-in toast when a user-initiated action fails. */
  onFailedAction?: (failure: FailedTransactionAction) => void;
  mutations?: TransactionMutationActions;
}

function httpStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  // Angular reports network failures as status 0; that is not an HTTP answer.
  return typeof status === 'number' && status > 0 ? status : null;
}

export class TransactionDataSource extends DataSource<TransactionListRow> {
  private subscription?: Subscription;
  private bucket?: Bucket;
  private _transactionPage: ReplaySubject<TransactionPage> = new ReplaySubject(1);
  public transactionPage$: Observable<TransactionPage> = this._transactionPage.asObservable().pipe(
    distinctUntilChanged((a, b) => deepEqual(a, b)),
    shareReplay(1),
  );
  public transactions$ = this.transactionPage$.pipe(
    filter(p => !!p && !!p.data),
    map(page => page.data.filter(t => !!t)),
  );

  public isUnbucketedOnly$: Observable<boolean> = this.transactionPage$.pipe(
    map(transactionPage => !!transactionPage?.params?.unbucketedOnly),
  );

  public unsortedTransactions$ = this.transactions$.pipe(map(list => list.filter(t => !t.bucketId)));

  /** Failed actions, for consumers that want richer handling than the built-in toast. */
  public readonly actionErrors$ = new Subject<{ action: Action; error: unknown }>();

  /** Successful non-scroll actions, for consumers that want success feedback (e.g. a sort toast). */
  public readonly actionSuccesses$ = new Subject<{ action: Action }>();

  private _pendingBuckets = new BehaviorSubject<Map<string, Bucket>>(new Map());
  public pendingBuckets$ = this._pendingBuckets.asObservable();

  public setPendingBucket(transactionId: string, bucket: Bucket | null) {
    const current = this._pendingBuckets.value;
    const existing = current.get(transactionId);
    if (!bucket && !existing) return;
    if (bucket && existing?.id === bucket.id) return;
    const next = new Map(current);
    if (bucket) next.set(transactionId, bucket);
    else next.delete(transactionId);
    this._pendingBuckets.next(next);
  }

  public clearPendingBuckets(transactionIds: string[]) {
    const current = this._pendingBuckets.value;
    if (!transactionIds.some(id => current.has(id))) return;
    const next = new Map(current);
    transactionIds.forEach(id => next.delete(id));
    this._pendingBuckets.next(next);
  }

  public pendingForDate$(date: string): Observable<Array<{ transactionId: string; bucketId: string }>> {
    return combineLatest([this.transactions$, this._pendingBuckets]).pipe(
      map(([transactions, pending]) =>
        transactions
          .filter(t => t.date === date)
          .map(t => {
            const bucketId = this.pendingBucketIdForTransaction(t, pending);
            if (bucketId) return { transactionId: t.id, bucketId };
            return null;
          })
          .filter((v): v is { transactionId: string; bucketId: string } => !!v),
      ),
    );
  }

  public pendingAcceptances$(): Observable<Array<{ transactionId: string; date: string; bucketId: string }>> {
    return combineLatest([this.transactions$, this._pendingBuckets]).pipe(
      map(([transactions, pending]) =>
        transactions
          .map(transaction => {
            const bucketId = this.pendingBucketIdForTransaction(transaction, pending);
            if (!bucketId) return null;
            return { transactionId: transaction.id, date: transaction.date, bucketId };
          })
          .filter((v): v is { transactionId: string; date: string; bucketId: string } => !!v),
      ),
    );
  }

  private pendingBucketIdForTransaction(transaction: Transaction, pending: Map<string, Bucket>): string | null {
    const override = pending.get(transaction.id);
    if (override) return override.id;
    if (transaction.bucketId) return null;
    if (transaction.selectedBucket) return transaction.selectedBucket.id;
    return transaction.suggestedBucketIds?.[0] ?? null;
  }

  private _reducer: Subject<Action> = new Subject();
  private _transactionsLoading: ReplaySubject<boolean> = new ReplaySubject();
  transactionsLoading$ = this._transactionsLoading.asObservable();
  private _selectedTransactionIds: BehaviorSubject<Set<string>> = new BehaviorSubject(new Set());
  private _multiSelectEnabled = new BehaviorSubject(false);
  public readonly multiSelectEnabled$ = this._multiSelectEnabled.asObservable().pipe(distinctUntilChanged());
  public selectedTransactions$: Observable<Transaction[]> = combineLatest([
    this.transactions$,
    this._selectedTransactionIds,
  ]).pipe(map(([transactions, selectedIds]) => transactions.filter(t => selectedIds.has(t.id))));

  private _loadingMoreTransactions: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loadingMoreTransactions$ = this._loadingMoreTransactions.asObservable();

  private transactionsWithDateHeaders$: Observable<TransactionListRow[]> = this.transactions$.pipe(
    map(transactions => {
      const totalsByDate = new Map<string, { total: number; count: number }>();
      transactions.forEach(t => {
        const agg = totalsByDate.get(t.date) ?? { total: 0, count: 0 };
        agg.total += t.netValue ?? 0;
        agg.count += 1;
        totalsByDate.set(t.date, agg);
      });
      return transactions.reduce<TransactionListRow[]>((items, transaction) => {
        const previousItem = items.length === 0 ? null : items[items.length - 1];
        if (previousItem && previousItem.date === transaction.date) return [...items, transaction];
        const agg = totalsByDate.get(transaction.date);
        const dateHeader: TransactionDateHeader = {
          _objectType: 'dateHeader',
          date: transaction.date,
          total: agg?.total,
          count: agg?.count,
        };
        return [...items, dateHeader, transaction];
      }, []);
    }),
  );

  constructor(
    starting: TransactionPage | Bucket,
    private transactionService: TransactionService,
    private bucketService: BucketService,
    private options?: TransactionDataSourceOptions,
  ) {
    super();

    if (starting._objectType === 'bucket') {
      this.bucket = starting as Bucket;
      this._transactionPage.next(this.bucket.transactions);
    } else this._transactionPage.next(starting as TransactionPage);

    this._reducer
      .pipe(
        switchMap(action =>
          defer(() => this.reduce(action)).pipe(
            take(1),
            // A failed action must not kill this subscription — otherwise every
            // later dispatch (saves included) silently no-ops until a reload.
            catchError(error => {
              console.error('Transaction action failed', { action: action.type, error });
              this._transactionsLoading.next(false);
              this.actionErrors$.next({ action, error });
              toast.error("That change didn't save. Please try again.");
              return EMPTY;
            }),
            // Runs on completion and when a later action cancels this one, so a
            // cancelled save cannot leave the list stuck in its loading state.
            finalize(() => this._transactionsLoading.next(false)),
          ),
        ),
      )
      .subscribe(({ page, action }) => {
        const retained = new Map(page.data.map(transaction => [transaction.id, transaction]));
        for (const [id, exposure] of this.displayedPredictions) {
          const transaction = retained.get(id);
          if (!transaction || transaction.bucketId || transaction.suggestionPredictionId !== exposure.predictionId)
            this.displayedPredictions.delete(id);
        }
        this._transactionPage.next(page);
        if (action.type !== 'SCROLL_EVENT') this.actionSuccesses$.next({ action });
        if (action.type !== 'SCROLL_EVENT' && this.options?.refreshSession)
          void this.options.refreshSession().catch((): void => undefined);
      });
  }

  connect(collectionViewer: CollectionViewer): Observable<TransactionListRow[]> {
    this.subscription = collectionViewer.viewChange
      .pipe(map(range => range.end))
      .subscribe(end => this.dispatch({ type: 'SCROLL_EVENT', value: end }));

    if (this.options && this.options.withoutDateHeaders) return this.transactions$;
    return this.transactionsWithDateHeaders$;
  }

  disconnect(_collectionViewer?: CollectionViewer): void {
    this.subscription?.unsubscribe();
    this.displayedPredictions.clear();
  }

  public dispatch(action: Action) {
    this._reducer.next(action);
  }

  toggleSelected({ isSelected, transaction }: { isSelected: boolean; transaction: Transaction }) {
    if (isSelected) this._multiSelectEnabled.next(true);
    this._selectedTransactionIds.pipe(take(1)).subscribe(selectedIds => {
      const nextSelectedIds = new Set(selectedIds);
      isSelected ? nextSelectedIds.add(transaction.id) : nextSelectedIds.delete(transaction.id);
      this._selectedTransactionIds.next(nextSelectedIds);
      if (nextSelectedIds.size === 0) this._multiSelectEnabled.next(false);
    });
  }

  clearSelected() {
    this._selectedTransactionIds.next(new Set());
    this._multiSelectEnabled.next(false);
  }

  setMultiSelectEnabled(enabled: boolean) {
    if (!enabled) {
      this.clearSelected();
      return;
    }
    this._multiSelectEnabled.next(true);
  }

  selectAll() {
    this._multiSelectEnabled.next(true);
    this.transactionPage$
      .pipe(
        take(1),
        map(page => this._selectedTransactionIds.next(new Set(page.data.map(t => t.id)))),
      )
      .subscribe();
  }

  isSelected$(transaction: Transaction): Observable<boolean> {
    return this._selectedTransactionIds.pipe(map(selected => selected.has(transaction.id)));
  }

  private readonly displayedPredictions = new Map<string, { predictionId: string; bucketIds: string[] }>();

  recordSuggestionDisplayed(transaction: Transaction) {
    if (!transaction.suggestionPredictionId || !transaction.suggestedBucketIds?.length) return;
    this.displayedPredictions.set(transaction.id, {
      predictionId: transaction.suggestionPredictionId,
      bucketIds: transaction.suggestedBucketIds.slice(0, 1),
    });
  }

  private predictionFeedback(transactions: Transaction[], method: CategorizationMethod) {
    const suggestions = transactions.flatMap(transaction => {
      if (!transaction.suggestionPredictionId) return [];
      const displayed = this.displayedPredictions.get(transaction.id);
      return [
        {
          transactionId: transaction.id,
          predictionId: transaction.suggestionPredictionId,
          rankedBucketIds: [...(transaction.suggestedBucketIds ?? [])],
          ...(displayed?.predictionId === transaction.suggestionPredictionId
            ? { displayedBucketIds: displayed.bucketIds }
            : {}),
        },
      ];
    });
    return suggestions.length ? { categorizationFeedback: { method, suggestions } } : {};
  }

  private reduce(action: Action): Observable<{ page: TransactionPage; action: Action }> {
    let page$: Observable<TransactionPage>;
    switch (action.type) {
      case 'SCROLL_EVENT':
        page$ = this.scrollEvent$(action.value);
        break;
      case 'DELETE_TRANSACTIONS':
        page$ = this.deleteTransactions$(action.value, action);
        break;
      case 'DELETE_TRANSFERS':
        page$ = this.deleteTransfers$(action.value, action);
        break;
      case 'RESTORE_TRANSACTIONS':
        page$ = this.undeleteTransactions$(action.value, action);
        break;
      case 'EDIT_TRANSACTION':
        page$ = this.editTransaction$(action.value, action);
        break;
      case 'EDIT_MANY_TRANSACTIONS':
        page$ = this.editManyTransactions$(action.value, action);
        break;
      case 'ACCEPT_PENDING':
        page$ = this.acceptPending$(action.value, action);
        break;
      case 'REFRESH_TRANSACTIONS':
      case 'CANCEL':
        page$ = this.refreshTransactions$();
        break;
    }

    return page$.pipe(
      map(page => ({ page, action })),
      tap(() => this._transactionsLoading.next(false)),
    );
  }

  private scrollEvent$(rangeEnd: number) {
    return of(rangeEnd).pipe(
      withLatestFrom(this.transactionPage$, this.transactionsWithDateHeaders$),
      filter(() => !this.options || !this.options.loadFirstPageOnly),
      switchMap(([rangeEnd, page, listItems]) => {
        if (listItems.length <= rangeEnd && page.totalRecords > page.data.length)
          return this.loadMoreTransactions$(page);
        return EMPTY;
      }),
    );
  }

  private acceptPending$(
    value: Array<{ transactionId: string; bucketId: string | null }>,
    action: Action,
  ): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        if (!value.length) return of(page);
        const groups = new Map<string | null, string[]>();
        value.forEach(({ transactionId, bucketId }) => {
          const ids = groups.get(bucketId) ?? [];
          ids.push(transactionId);
          groups.set(bucketId, ids);
        });
        const transactionsById = new Map(page.data.map(transaction => [transaction.id, transaction]));
        const requests = Array.from(groups.entries()).map(([bucketId, ids]) => {
          const previousTransactions = ids
            .map(id => transactionsById.get(id))
            .filter((transaction): transaction is Transaction => !!transaction);
          const unsortedDelta = ids.reduce((delta, id) => {
            const previousTransaction = transactionsById.get(id);
            if (!previousTransaction) return bucketId === null ? delta + 1 : delta;
            return delta + this.unsortedCountDelta([previousTransaction], bucketId);
          }, 0);
          return {
            bucketId,
            ids,
            previousTransactions,
            unsortedDelta,
            request: this.mutations.updateMultipleTransactions(
              { ids, bucketId, ...this.predictionFeedback(previousTransactions, 'accept_pending') },
              previousTransactions,
            ),
          };
        });
        this.applyUnsortedCountChange(requests.reduce((delta, group) => delta + group.unsortedDelta, 0));
        const settled = Promise.allSettled(requests.map(({ request }) => request));
        // Each group is its own server call, so only the groups that failed roll back.
        void settled.then(results =>
          this.applyUnsortedCountChange(
            -requests.reduce(
              (delta, group, index) => (results[index]?.status === 'rejected' ? delta + group.unsortedDelta : delta),
              0,
            ),
          ),
        );
        this.trackMutation(action, Promise.all(requests.map(({ request }) => request)));
        this.reportAfterSuccessfulCategorizationBatch(
          requests.map(request => ({
            request: request.request,
            previousTransactions: request.previousTransactions,
            bucketId: request.bucketId,
          })),
        );
        return from(settled).pipe(
          switchMap(results => {
            const succeededIds = requests.flatMap((group, index) =>
              results[index]?.status === 'fulfilled' ? group.ids : [],
            );
            if (succeededIds.length === 0) return EMPTY;
            const allNew = results.flatMap(result => (result.status === 'fulfilled' ? result.value.data : []));
            const updatedPage = this.replaceChangedTransactions(page, succeededIds, allNew);
            this.clearPendingBuckets(succeededIds);
            return this.reloadLastPage$(updatedPage);
          }),
        );
      }),
    );
  }

  private editManyTransactions$(value: EditManyTransactionsActionValue, action: Action): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        const previousTransactions = page.data.filter(transaction => value.ids.includes(transaction.id));
        const unsortedDelta = this.unsortedCountDelta(previousTransactions, value.bucketId);
        this.applyUnsortedCountChange(unsortedDelta);
        const request = this.mutations.updateMultipleTransactions(
          { ...value, ...this.predictionFeedback(previousTransactions, 'bulk_edit') },
          previousTransactions,
        );
        this.trackMutation(action, request, () => this.applyUnsortedCountChange(-unsortedDelta));
        this.reportAfterSuccessfulCategorization(request, 'bulk_edit', result =>
          this.categorizedImportedOutcome(previousTransactions, value.bucketId, result.data),
        );
        return from(request).pipe(
          catchError(() => EMPTY),
          map(result => this.replaceChangedTransactions(page, value.ids, result.data)),
          switchMap(page => this.reloadLastPage$(page)),
        );
      }),
    );
  }

  private editTransaction$(transaction: UpdateTransactionPayload, action: Action): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        const previous = page.data.filter(candidate => candidate.id === transaction.id);
        const unsortedDelta = this.unsortedCountDelta(previous, transaction.bucketId);
        this.applyUnsortedCountChange(unsortedDelta);
        const request = this.mutations.updateTransaction({
          ...transaction,
          ...this.predictionFeedback(previous, 'single_edit'),
        });
        this.trackMutation(action, request, () => this.applyUnsortedCountChange(-unsortedDelta));
        this.reportAfterSuccessfulCategorization(request, 'single_edit', updatedTransaction =>
          this.categorizedImportedOutcome(previous, transaction.bucketId, [updatedTransaction]),
        );
        return from(request).pipe(
          catchError(() => EMPTY),
          map(updatedTransaction => {
            const normalizedTransaction = this.withChildParentRelations(updatedTransaction);
            return this.replaceChangedTransactions(
              page,
              [
                transaction.id,
                ...(transaction.children || []).map(t => t.id),
                ...(normalizedTransaction.children || []).map(t => t.id),
              ].filter((id): id is string => !!id),
              [normalizedTransaction, ...(normalizedTransaction.children || [])],
            );
          }),
          switchMap(this.reloadLastPage$.bind(this)),
        );
      }),
    );
  }

  // undefined bucketId means the payload doesn't touch categorization; null is an explicit clear.
  private unsortedCountDelta(previousTransactions: Transaction[], bucketId: string | null | undefined): number {
    if (bucketId === undefined) return 0;
    if (bucketId === null) return previousTransactions.filter(transaction => !!transaction.bucketId).length;
    return -previousTransactions.filter(transaction => !transaction.bucketId).length;
  }

  private applyUnsortedCountChange(delta: number): void {
    if (delta !== 0) this.options?.onUnsortedCountChange?.(delta);
  }

  private categorizedImportedOutcome(
    transactions: Transaction[],
    bucketId: string | null | undefined,
    updatedTransactions: Transaction[],
  ): Omit<CategorizationOutcome, 'method'> {
    const updatedById = new Map(updatedTransactions.map(transaction => [transaction.id, transaction]));
    // Follow successful saves even when the reducer subscription was cancelled.
    for (const transaction of transactions) {
      const updated = updatedById.get(transaction.id);
      if (updated && updated.bucketId !== transaction.bucketId) this.displayedPredictions.delete(transaction.id);
    }
    const categorized = transactions.filter(
      transaction =>
        hasTransactionSyncedFinancialFacts(transaction) &&
        ((bucketId != null &&
          transaction.bucketId !== bucketId &&
          updatedById.get(transaction.id)?.bucketId === bucketId) ||
          this.hasCategorizedChildChange(transaction, updatedById.get(transaction.id))),
    );
    const suggestedCount = categorized.filter(transaction => {
      const newBucketId = updatedById.get(transaction.id)?.bucketId;
      return !!newBucketId && (transaction.suggestedBucketIds ?? []).includes(newBucketId);
    }).length;
    return { transactionCount: categorized.length, suggestedCount };
  }

  private hasCategorizedChildChange(previous: Transaction, updated: Transaction | undefined): boolean {
    if (!updated?.children?.length) return false;
    const previousChildren = new Map((previous.children ?? []).map(child => [child.id, child]));
    return updated.children.some(child => {
      if (!child.bucketId) return false;
      const previousChild = child.id ? previousChildren.get(child.id) : undefined;
      if (previousChild) return previousChild.bucketId !== child.bucketId;
      return previous.bucketId == null || child.bucketId !== previous.bucketId;
    });
  }

  private reportSuccessfulCategorization(outcome: CategorizationOutcome): void {
    if (outcome.transactionCount <= 0) return;
    try {
      this.options?.onSuccessfulCategorization?.(outcome);
    } catch (error) {
      console.error('Categorization analytics failed', error);
    }
  }

  /**
   * Everything a failed save owes the user follows the mutation promise, not the
   * reducer stream: the reducer's switchMap drops an in-flight action's observable
   * when a later action (even a scroll) arrives, so a rejection landing afterwards
   * would otherwise reach neither the toast, the badge rollback, nor telemetry.
   * The observable side swallows the same rejection so nothing is applied twice.
   * One report per action, however many requests it fans out to.
   */
  private trackMutation(action: Action, request: Promise<unknown>, rollback?: () => void): void {
    if (action.type === 'SCROLL_EVENT') return;
    void request.then(
      (): void => undefined,
      error => {
        console.error('Transaction action failed', { action: action.type, error });
        rollback?.();
        // Loading is owned by the reducer's finalize: a stale rejection must not
        // clear the indicator while a newer action is still in flight.
        this.actionErrors$.next({ action, error });
        toast.error("That change didn't save. Please try again.");
        try {
          this.options?.onFailedAction?.({ action: action.type, status: httpStatus(error) });
        } catch (reportError) {
          console.error('Failed-action analytics failed', reportError);
        }
      },
    );
  }

  private reportAfterSuccessfulCategorization<T>(
    request: Promise<T>,
    method: CategorizationMethod,
    outcome: (result: T) => Omit<CategorizationOutcome, 'method'>,
  ): void {
    void request.then(
      result => this.reportSuccessfulCategorization({ ...outcome(result), method }),
      (): void => undefined,
    );
  }

  private reportAfterSuccessfulCategorizationBatch(
    requests: Array<{
      request: Promise<{ data: Transaction[] }>;
      previousTransactions: Transaction[];
      bucketId: string | null;
    }>,
  ): void {
    if (requests.length === 0) return;
    let settled = 0;
    const total: CategorizationOutcome = { transactionCount: 0, suggestedCount: 0, method: 'accept_pending' };
    requests.forEach(({ request, previousTransactions, bucketId }) => {
      void request
        .then(
          result => {
            const outcome = this.categorizedImportedOutcome(previousTransactions, bucketId, result.data);
            total.transactionCount += outcome.transactionCount;
            total.suggestedCount += outcome.suggestedCount;
          },
          (): void => undefined,
        )
        .finally(() => {
          settled += 1;
          if (settled === requests.length) this.reportSuccessfulCategorization(total);
        });
    });
  }

  private withChildParentRelations(transaction: Transaction): Transaction {
    const children = transaction.children || [];
    if (children.length === 0) return transaction;

    const parent = { ...transaction, children };
    return {
      ...transaction,
      children: children.map(child => ({
        ...child,
        parent: child.parent || parent,
      })),
    };
  }

  private undeleteTransactions$(transactions: Transaction[], action: Action): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        const requests = transactions.map(transaction =>
          this.mutations.undeleteTransaction(transaction.id, DateTime.fromISO(transaction.date).toFormat('yyyy-MM')),
        );
        return this.removeSettled$(
          page,
          transactions.map(t => t.id),
          requests,
          action,
        );
      }),
    );
  }

  private deleteTransfers$(
    transactions: DeleteTransferActionTransaction[],
    action: Action,
  ): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        const requests = transactions.map(transaction =>
          this.mutations.deleteTransfer(
            transaction.id,
            transaction.date ? DateTime.fromISO(transaction.date).toFormat('yyyy-MM') : undefined,
          ),
        );
        return this.removeSettled$(
          page,
          transactions.map(t => t.id),
          requests,
          action,
        );
      }),
    );
  }

  /**
   * Each row is its own server call. Wait for all of them, drop only the rows the
   * server confirmed, and reload authoritative state whenever anything succeeded;
   * failures are reported once through trackMutation.
   */
  private removeSettled$(
    page: TransactionPage,
    ids: string[],
    requests: Promise<unknown>[],
    action: Action,
  ): Observable<TransactionPage> {
    this.trackMutation(action, Promise.all(requests));
    return from(Promise.allSettled(requests)).pipe(
      switchMap(results => {
        const removedIds = ids.filter((_, index) => results[index]?.status === 'fulfilled');
        if (removedIds.length === 0) return EMPTY;
        return this.reloadLastPage$({ ...page, data: page.data.filter(t => !removedIds.includes(t.id)) });
      }),
    );
  }

  private deleteTransactions$(transactions: Transaction[], action: Action): Observable<TransactionPage> {
    this._transactionsLoading.next(true);
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => {
        const requests = transactions.map(transaction =>
          this.mutations.deleteTransaction(transaction.id, DateTime.fromISO(transaction.date).toFormat('yyyy-MM')),
        );
        return this.removeSettled$(
          page,
          transactions.map(t => t.id),
          requests,
          action,
        );
      }),
    );
  }

  private refreshTransactions$(): Observable<TransactionPage> {
    return this.transactionPage$.pipe(
      take(1),
      switchMap(page => this.reloadLastPage$(page)),
    );
  }

  private reloadLastPage$(page: TransactionPage): Observable<TransactionPage> {
    if (this.bucket) {
      return this.bucketService.fetchBucket$(this.bucket.id, this.bucket.monthYear).pipe(
        tap(b => (this.bucket = b)),
        map(b => b.transactions),
      );
    }
    if (this.options?.refreshTransactions) {
      this._transactionsLoading.next(true);
      return from(this.options.refreshTransactions()).pipe(
        map(refreshedPage => refreshedPage ?? page),
        catchError(() => of(page)),
        finalize(() => this._transactionsLoading.next(false)),
      );
    }

    const newPageNumber = page.pageNumber - 1;
    return this.loadMoreTransactions$({
      ...page,
      pageNumber: newPageNumber,
      data: page.data.slice(0, newPageNumber * page.pageSize),
    });
  }

  private loadMoreTransactions$(page: TransactionPage): Observable<TransactionPage> {
    if (this.bucket) return of(page);
    this._loadingMoreTransactions.next(true);
    this._transactionsLoading.next(true);
    if (this.options?.loadMoreTransactions) {
      return from(this.options.loadMoreTransactions()).pipe(
        map(nextPage => nextPage ?? page),
        catchError(() => of(page)),
        finalize(() => {
          this._loadingMoreTransactions.next(false);
          this._transactionsLoading.next(false);
        }),
      );
    }

    this._loadingMoreTransactions.next(false);
    this._transactionsLoading.next(false);
    return of(page);
  }

  private get mutations(): TransactionMutationActions {
    if (!this.options?.mutations) throw new Error('TransactionDataSource requires TanStack-backed mutation actions.');
    return this.options.mutations;
  }

  // Assumes sort is always descending by date+id.
  private replaceChangedTransactions(
    page: TransactionPage,
    oldTransactionIds: string[],
    newTransactions: Transaction[],
  ): TransactionPage {
    const idsToRemove = oldTransactionIds;
    let transactionsToAdd = newTransactions
      .filter(t => !t.parent && this.transactionService.matchesFilters(t, page.params))
      .sort((t1, t2) => (`${t1.date}${t1.id}` < `${t2.date}${t2.id}` ? 1 : -1));

    const updatedList = page.data
      .filter(t => idsToRemove.indexOf(t.id) === -1)
      .reduce<Transaction[]>((curr, t) => {
        const toAdd = [] as Transaction[];
        [...transactionsToAdd].forEach(t2 => {
          if (`${t.date}${t.id}` <= `${t2.date}${t2.id}`) {
            toAdd.push(t2);
            transactionsToAdd = transactionsToAdd.filter(t => t.id !== t2.id);
          }
        });
        return [...curr, ...toAdd, t];
      }, []);
    return { ...page, data: [...updatedList, ...transactionsToAdd] };
  }
}
